import React, { useState, useRef, useEffect } from "react";
import { EyeInvisible } from "@/icons/eyeinvisible";
import { Eye } from "@/icons/eye";
import styles from "./css/inputFields.module.css";
import { useTranslation } from "react-i18next";
import { langDirection } from "@/utils/languages";
import { Tooltip } from "@mui/material";

interface InputFieldProps {
  inputType: "text" | "password" | "number";
  disabled?: boolean;
  placeholder?: string;
  data: string | number | undefined;
  setData: (value: string | undefined) => void;
  id: string;
  className?: string;
  errorMessage?: string;
  showError?: boolean;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  small?: boolean;
  tooltip?: string;
}

export const InputField: React.FC<InputFieldProps> = ({
  inputType,
  disabled = false,
  placeholder = "",
  data,
  setData,
  id,
  className = "",
  errorMessage = "",
  showError = false,
  onKeyDown,
  small = false,
  tooltip,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { i18n } = useTranslation("LanguageSwitcher");
  const currentLanguage = i18n.language;
  const direction = langDirection(currentLanguage);

  const determinedInputType =
    inputType === "password" ? (isVisible ? "text" : "password") : inputType;

  const iconColor = isFocused ? "#1890ff" : "#acabba";

  const handleFocus = () => {
    if (!disabled) {
      setIsFocused(true);
    }
  };

  const handleBlur = () => {
    if (!disabled) {
      setIsFocused(false);
    }
  };

  const toggleVisibility = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    if (!disabled) {
      event.preventDefault();
      setIsVisible(!isVisible);
    }
  };

  const handleChange = () => {
    if (inputRef.current) {
      const value = inputRef.current.value;
      
      if (inputType === 'number' && value !== '') {
        const isValidNumber = /^[0-9]*\.?[0-9]*$/.test(value);
        const numericValue = parseFloat(value);
        
        if (!isValidNumber || numericValue < 0) {
          return;
        }
      }
      
      if (inputType === 'number' && value === '') {
        setData(undefined);
      } else {
        setData(value);
      }
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (inputType === 'number') {
      const allowedKeys = [
        'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
        'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
        'Home', 'End'
      ];
      
      const isNumber = event.key >= '0' && event.key <= '9';
      const isDecimalPoint = event.key === '.' && inputRef.current && !inputRef.current.value.includes('.');
      const isCopyPaste = (event.ctrlKey || event.metaKey) && (event.key === 'c' || event.key === 'v' || event.key === 'x' || event.key === 'a');
      
      if (!allowedKeys.includes(event.key) && !isNumber && !isDecimalPoint && !isCopyPaste) {
        event.preventDefault();
      }
      
      if (event.key === '-') {
        event.preventDefault();
      }
    }
    
    if (onKeyDown) {
      onKeyDown(event);
    }
  };

  const handleWheel = (event: React.WheelEvent<HTMLInputElement>) => {
    // מניעת שינוי ערכים בגלילה עבור שדות מספר
    if (inputType === 'number') {
      event.currentTarget.blur();
    }
  };

  useEffect(() => {
    if(inputRef.current && data !== undefined && data !== null) {
      inputRef.current.value = data.toString();
    } else if (inputRef.current) {
      inputRef.current.value = '';
    }
  }, [data])

  const fieldContent = (
    <div className={`${styles.wrapper} ${small ? styles.smallWrapper : ""}`}>
      <div className={`${styles.fieldContainer}`}>
        <div
          className={`${styles.fieldInnerContainer} ${
            inputType === "text" ? styles.fieldInnerContainerText : ""
          } ${disabled ? styles.fieldInputContainerDisabled : ""}`}
        >
          <div
            className={`${styles.fieldInputContainer} ${className} ${
              showError && errorMessage ? styles.fieldInputContainerError : ""
            } ${
              disabled
                ? styles.fieldInputContainerDisabled
                : isFocused
                  ? styles.fieldInputContainerActive
                  : styles.fieldInputContainerDefault
            }`}
          >
            <input
              data-testid={id}
              id={id}
              ref={inputRef}
              className={`${styles.fieldInput} ${
                disabled ? styles.fieldInputDisabled : styles.fieldInputDefault
              } ${styles[direction]}`}
              placeholder={placeholder}
              type={determinedInputType}
              onChange={handleChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              onWheel={handleWheel}
              disabled={disabled}
              dir={direction}
            />

            {inputType === "password" && (
              <div
                onMouseDown={toggleVisibility}
                className={`${styles.icon} ${styles[direction]}`}
                style={{ cursor: disabled ? "default" : "pointer" }}
              >
                {isVisible ? (
                  <Eye color={iconColor} />
                ) : (
                  <EyeInvisible color={iconColor} />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {showError && (
        <div className={styles.errorMessageContainer}>
          <div
            className={`${styles.errorMessage} ${errorMessage ? styles.visible : ""}`}
          >
            {errorMessage}
          </div>
        </div>
      )}
    </div>
  );

  return tooltip ? (
    <Tooltip title={tooltip} placement="top" arrow>
      {fieldContent}
    </Tooltip>
  ) : (
    fieldContent
  );
};
